/**
 * Pending Member Detail Screen
 * View and manage a pre-registered family member
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useRouter, useLocalSearchParams } from "expo-router";
import { useThemeColors } from "@/providers/ThemeProvider";
import { supabase } from "@/lib/supabase/client";
import { usePendingMembers } from "@/modules/pending-members";
import type { PendingMemberWithRelations } from "@/modules/pending-members";

export default function PendingMemberDetailScreen() {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const colors = useThemeColors();
  const { deletePendingMember } = usePendingMembers();

  const [member, setMember] = useState<PendingMemberWithRelations | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch member details
  const fetchMember = useCallback(async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("pending_members")
        .select(`
          *,
          team:teams(id, name, color),
          weekend:family_weekends(id, name),
          invited_by_profile:profiles!pending_members_invited_by_fkey(id, full_name, nickname),
          claimed_by_profile:profiles!pending_members_claimed_by_fkey(id, full_name, nickname, email)
        `)
        .eq("id", id)
        .single();

      if (error) throw error;
      setMember(data);
    } catch (error) {
      console.error("Error fetching pending member:", error);
      Alert.alert("Fout", "Kon lid niet ophalen");
      router.back();
    } finally {
      setIsLoading(false);
    }
  }, [id, router]);

  useEffect(() => {
    fetchMember();
  }, [fetchMember]);

  /**
   * Handle delete
   */
  const handleDelete = () => {
    if (!member) return;

    Alert.alert(
      "Verwijderen",
      `Weet je zeker dat je de pre-registratie voor ${member.full_name} wilt verwijderen?`,
      [
        { text: "Annuleren", style: "cancel" },
        {
          text: "Verwijderen",
          style: "destructive",
          onPress: async () => {
            const success = await deletePendingMember(member.id);
            if (success) {
              router.back();
            }
          },
        },
      ]
    );
  };

  if (isLoading) {
    return (
      <View className="flex-1 items-center justify-center" style={{ backgroundColor: colors.background }}>
        <ActivityIndicator size="large" color="#DC2626" />
      </View>
    );
  }

  if (!member) {
    return (
      <View className="flex-1 items-center justify-center" style={{ backgroundColor: colors.background }}>
        <Ionicons name="person-outline" size={48} color={colors.textSecondary} />
        <Text className="mt-4" style={{ color: colors.textSecondary }}>
          Lid niet gevonden
        </Text>
      </View>
    );
  }

  const isClaimed = member.claimed_at !== null;

  return (
    <ScrollView
      className="flex-1 px-4 py-4"
      style={{ backgroundColor: colors.background }}
    >
      {/* Status Badge */}
      <View
        className="rounded-xl p-4 mb-4 flex-row items-center"
        style={{
          backgroundColor: isClaimed ? "#10B98120" : "#F5920520",
        }}
      >
        <View
          className="w-12 h-12 rounded-full items-center justify-center mr-4"
          style={{ backgroundColor: isClaimed ? "#10B98130" : "#F5920530" }}
        >
          <Ionicons
            name={isClaimed ? "checkmark-circle" : "time-outline"}
            size={28}
            color={isClaimed ? "#10B981" : "#F59205"}
          />
        </View>
        <View className="flex-1">
          <Text
            className="font-semibold text-lg"
            style={{ color: isClaimed ? "#10B981" : "#F59205" }}
          >
            {isClaimed ? "Aangemeld" : "Wachtend op aanmelding"}
          </Text>
          {isClaimed && member.claimed_at && (
            <Text className="text-sm" style={{ color: colors.textSecondary }}>
              Op{" "}
              {new Date(member.claimed_at).toLocaleDateString("nl-NL", {
                day: "numeric",
                month: "long",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </Text>
          )}
        </View>
      </View>

      {/* Claimed User Info */}
      {isClaimed && member.claimed_by_profile && (
        <View
          className="rounded-xl p-4 mb-4"
          style={{ backgroundColor: colors.surface }}
        >
          <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
            Aangemeld als
          </Text>
          <TouchableOpacity
            onPress={() => router.push(`/(super-admin)/users/${member.claimed_by}`)}
            className="flex-row items-center"
          >
            <View
              className="w-10 h-10 rounded-full items-center justify-center mr-3"
              style={{ backgroundColor: colors.primary + "20" }}
            >
              <Ionicons name="person" size={20} color={colors.primary} />
            </View>
            <View className="flex-1">
              <Text className="font-medium" style={{ color: colors.text }}>
                {member.claimed_by_profile.full_name || member.claimed_by_profile.nickname}
              </Text>
              <Text className="text-sm" style={{ color: colors.textSecondary }}>
                {member.claimed_by_profile.email}
              </Text>
            </View>
            <Ionicons name="chevron-forward" size={20} color={colors.textSecondary} />
          </TouchableOpacity>
        </View>
      )}

      {/* Basic Info */}
      <View
        className="rounded-xl p-4 mb-4"
        style={{ backgroundColor: colors.surface }}
      >
        <Text className="text-lg font-bold mb-4" style={{ color: colors.text }}>
          Gegevens
        </Text>

        <InfoRow label="E-mail" value={member.email} icon="mail-outline" />
        <InfoRow label="Naam" value={member.full_name} icon="person-outline" />
        {member.nickname && (
          <InfoRow label="Bijnaam" value={member.nickname} icon="happy-outline" />
        )}
        {member.phone && (
          <InfoRow label="Telefoon" value={member.phone} icon="call-outline" />
        )}
        {member.date_of_birth && (
          <InfoRow
            label="Geboortedatum"
            value={new Date(member.date_of_birth).toLocaleDateString("nl-NL", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
            icon="calendar-outline"
          />
        )}
      </View>

      {/* Team Info */}
      {member.team && (
        <View
          className="rounded-xl p-4 mb-4"
          style={{ backgroundColor: colors.surface }}
        >
          <Text className="text-lg font-bold mb-4" style={{ color: colors.text }}>
            Team
          </Text>
          <View className="flex-row items-center">
            <View
              className="w-10 h-10 rounded-full items-center justify-center mr-3"
              style={{ backgroundColor: member.team.color + "30" }}
            >
              <Ionicons name="people" size={20} color={member.team.color} />
            </View>
            <View>
              <Text className="font-medium" style={{ color: colors.text }}>
                {member.team.name}
              </Text>
              {member.is_captain && (
                <View className="flex-row items-center mt-1">
                  <Ionicons name="star" size={14} color="#F59E0B" />
                  <Text className="ml-1 text-sm" style={{ color: "#F59E0B" }}>
                    Kapitein
                  </Text>
                </View>
              )}
            </View>
          </View>
        </View>
      )}

      {/* Metadata */}
      <View
        className="rounded-xl p-4 mb-4"
        style={{ backgroundColor: colors.surface }}
      >
        <Text className="text-lg font-bold mb-4" style={{ color: colors.text }}>
          Info
        </Text>
        <InfoRow
          label="Aangemaakt"
          value={new Date(member.created_at).toLocaleDateString("nl-NL", {
            day: "numeric",
            month: "long",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
          icon="time-outline"
        />
        {member.invited_by_profile && (
          <InfoRow
            label="Toegevoegd door"
            value={member.invited_by_profile.nickname || member.invited_by_profile.full_name || "Onbekend"}
            icon="person-add-outline"
          />
        )}
        {member.weekend && (
          <InfoRow label="Weekend" value={member.weekend.name} icon="calendar-outline" />
        )}
      </View>

      {/* Actions */}
      {!isClaimed && (
        <View className="mb-8">
          <TouchableOpacity
            onPress={handleDelete}
            className="rounded-xl py-4 items-center flex-row justify-center"
            style={{ backgroundColor: "#EF444420" }}
          >
            <Ionicons name="trash-outline" size={20} color="#EF4444" />
            <Text className="ml-2 font-medium" style={{ color: "#EF4444" }}>
              Verwijderen
            </Text>
          </TouchableOpacity>
        </View>
      )}
    </ScrollView>
  );
}

/**
 * Info Row Component
 */
function InfoRow({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon: keyof typeof Ionicons.glyphMap;
}) {
  const colors = useThemeColors();

  return (
    <View className="flex-row items-center py-2">
      <Ionicons name={icon} size={18} color={colors.textSecondary} />
      <Text className="ml-3 text-sm" style={{ color: colors.textSecondary }}>
        {label}:
      </Text>
      <Text className="ml-2 flex-1" style={{ color: colors.text }}>
        {value}
      </Text>
    </View>
  );
}

